import React, { useState, useEffect } from 'react';
import Sidebar from '../components/Sidebar';
import Navbar from '../components/Navbar';
import { complaintsAPI } from '../api/complaintsAPI';
import { authAPI } from '../api/api';
import { toast } from 'react-toastify';
import {
  FiMessageCircle,
  FiClock,
  FiCheckCircle,
  FiLock,
  FiUnlock,
  FiSend,
  FiRefreshCw,
  FiPlus,
  FiX
} from 'react-icons/fi';
import '../css/Complaints.css';

const Complaints = ({ isSidebarClosed, setIsSidebarClosed }) => {
  const [complaints, setComplaints] = useState([]);
  const [selectedComplaint, setSelectedComplaint] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [messageText, setMessageText] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [showNewComplaintForm, setShowNewComplaintForm] = useState(false);
  const [newComplaintForm, setNewComplaintForm] = useState({
    title: '',
    description: ''
  });
  const [creatingComplaint, setCreatingComplaint] = useState(false);

  // Status configurations
  const statusConfig = {
    'open': {
      label: 'Nyitott',
      color: '#28a745',
      icon: <FiUnlock size={16} />
    },
    'in_progress': {
      label: 'Folyamatban',
      color: '#ffc107',
      icon: <FiClock size={16} />
    },
    'resolved': {
      label: 'Megoldott',
      color: '#17a2b8',
      icon: <FiCheckCircle size={16} />
    },
    'closed': {
      label: 'Lezárt',
      color: '#6c757d',
      icon: <FiLock size={16} />
    }
  };

  // Load complaints
  const loadComplaints = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await complaintsAPI.getComplaints();
      setComplaints(data);
    } catch (err) {
      setError(err.message || 'Hiba történt a reklamációk betöltésekor');
      toast.error(err.message || 'Hiba történt a reklamációk betöltésekor');
    } finally {
      setLoading(false);
    }
  };

  // Load complaint details
  const loadComplaintDetails = async (complaintId) => {
    try {
      const data = await complaintsAPI.getComplaint(complaintId);
      setSelectedComplaint(data);
    } catch (err) {
      toast.error(err.message || 'Hiba történt a reklamáció részleteinek betöltésekor');
    }
  };

  // Send message
  const handleSendMessage = async () => {
    if (!messageText.trim() || !selectedComplaint) return;

    try {
      setSendingMessage(true);
      await complaintsAPI.sendMessage(selectedComplaint.id, messageText.trim());

      toast.success('Üzenet sikeresen elküldve');
      setMessageText('');

      // Reload complaint details to show new message
      await loadComplaintDetails(selectedComplaint.id);

      // Refresh complaints list
      await loadComplaints();
    } catch (err) {
      toast.error(err.message || 'Hiba történt az üzenet küldésekor');
    } finally {
      setSendingMessage(false);
    }
  };

  // Refresh data
  const handleRefresh = async () => {
    setRefreshing(true);
    await loadComplaints();
    if (selectedComplaint) {
      await loadComplaintDetails(selectedComplaint.id);
    }
    setRefreshing(false);
    toast.success('Adatok frissítve');
  };

  // Create new complaint
  const handleCreateComplaint = async () => {
    if (!newComplaintForm.title.trim() || !newComplaintForm.description.trim()) {
      toast.error('Kérjük töltse ki minden mezőt');
      return;
    }

    try {
      setCreatingComplaint(true);
      await complaintsAPI.createComplaint(newComplaintForm.title.trim(), newComplaintForm.description.trim());

      toast.success('Reklamáció sikeresen létrehozva');
      setNewComplaintForm({ title: '', description: '' });
      setShowNewComplaintForm(false);

      // Refresh complaints list
      await loadComplaints();
    } catch (err) {
      toast.error(err.message || 'Hiba történt a reklamáció létrehozásakor');
    } finally {
      setCreatingComplaint(false);
    }
  };

  // Format date
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('hu-HU');
  };

  useEffect(() => {
    loadComplaints();
  }, []);

  const currentUser = authAPI.getCurrentUser();

  return (
    <div className="complaints-container">
          <div className="complaints-header">
            <h1>
              <FiMessageCircle className="page-icon" />
              Reklamációk
            </h1>
            <button
              className="new-complaint-btn"
              onClick={() => setShowNewComplaintForm(true)}
              disabled={showNewComplaintForm}
            >
              <FiPlus />
              Új reklamáció
            </button>
          </div>

          {error && (
            <div className="error-message">
              <p>{error}</p>
              <button onClick={loadComplaints}>Újrapróbálkozás</button>
            </div>
          )}

          {/* New Complaint Form */}
          {showNewComplaintForm && (
            <div className="new-complaint-form">
              <div className="form-header">
                <h3>Új reklamáció létrehozása</h3>
                <button
                  className="close-btn"
                  onClick={() => {
                    setShowNewComplaintForm(false);
                    setNewComplaintForm({ title: '', description: '' });
                  }}
                >
                  <FiX />
                </button>
              </div>
              <div className="form-content">
                <div className="form-group">
                  <label>Tárgy *</label>
                  <input
                    type="text"
                    value={newComplaintForm.title}
                    onChange={(e) => setNewComplaintForm(prev => ({...prev, title: e.target.value}))}
                    placeholder="Adja meg a reklamáció tárgyát"
                    disabled={creatingComplaint}
                  />
                </div>
                <div className="form-group">
                  <label>Leírás *</label>
                  <textarea
                    value={newComplaintForm.description}
                    onChange={(e) => setNewComplaintForm(prev => ({...prev, description: e.target.value}))}
                    placeholder="Írja le részletesen a problémát"
                    rows={6}
                    disabled={creatingComplaint}
                  />
                </div>
                <div className="form-actions">
                  <button
                    className="create-btn"
                    onClick={handleCreateComplaint}
                    disabled={creatingComplaint || !newComplaintForm.title.trim() || !newComplaintForm.description.trim()}
                  >
                    {creatingComplaint ? 'Létrehozás...' : 'Reklamáció létrehozása'}
                  </button>
                  <button
                    className="cancel-btn"
                    onClick={() => {
                      setShowNewComplaintForm(false);
                      setNewComplaintForm({ title: '', description: '' });
                    }}
                    disabled={creatingComplaint}
                  >
                    Mégse
                  </button>
                </div>
              </div>
            </div>
          )}

          <div className="complaints-content">
            {/* Complaints List */}
            <div className="complaints-list">
              <div className="list-header">
                <h3>Reklamációim ({complaints.length})</h3>
              </div>

              {loading ? (
                <div className="loading-spinner">
                  <div className="spinner"></div>
                  <p>Reklamációk betöltése...</p>
                </div>
              ) : complaints.length === 0 ? (
                <div className="empty-state">
                  <FiMessageCircle size={48} />
                  <h3>Nincs reklamáció</h3>
                  <p>Jelenleg nincsenek reklamációi.</p>
                </div>
              ) : (
                <div className="complaints-items">
                  {complaints.map((complaint) => (
                    <div
                      key={complaint.id}
                      className={`complaint-item ${selectedComplaint?.id === complaint.id ? 'active' : ''}`}
                      onClick={() => loadComplaintDetails(complaint.id)}
                    >
                      <div className="complaint-header">
                        <div className="complaint-number">
                          {complaint.complaint_number}
                        </div>
                        <div
                          className="complaint-status"
                          style={{
                            backgroundColor: statusConfig[complaint.status]?.color || '#6c757d',
                            color: 'white'
                          }}
                        >
                          {statusConfig[complaint.status]?.icon}
                          <span>{statusConfig[complaint.status]?.label || complaint.status}</span>
                        </div>
                      </div>

                      <div className="complaint-title">
                        {complaint.title}
                      </div>

                      <div className="complaint-meta">
                        <div className="complaint-date">
                          {formatDate(complaint.created_at)}
                        </div>
                        <div className="complaint-messages">
                          <FiMessageCircle size={14} />
                          {complaint.message_count} üzenet
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Complaint Details */}
            <div className="complaint-details">
              {selectedComplaint ? (
                <>
                  <div className="details-header">
                    <div className="complaint-info">
                      <h2>{selectedComplaint.title}</h2>
                      <div className="complaint-meta-details">
                        <span className="complaint-number-large">
                          {selectedComplaint.complaint_number}
                        </span>
                        <div
                          className="status-badge"
                          style={{
                            backgroundColor: statusConfig[selectedComplaint.status]?.color || '#6c757d',
                            color: 'white'
                          }}
                        >
                          {statusConfig[selectedComplaint.status]?.icon}
                          {statusConfig[selectedComplaint.status]?.label || selectedComplaint.status}
                        </div>
                      </div>
                      <div className="complaint-dates">
                        <div>Létrehozva: {formatDate(selectedComplaint.created_at)}</div>
                        <div>Ügyfél: {selectedComplaint.client_name}</div>
                      </div>
                    </div>
                  </div>

                  {/* Messages */}
                  <div className="messages-container">
                    <h3>Üzenetek</h3>
                    <div className="messages-list">
                      {selectedComplaint.messages && selectedComplaint.messages.length > 0 ? (
                        selectedComplaint.messages.map((message, index) => (
                          <div key={index} className="message-item">
                            <div className="message-content">
                              {message.content.split('\n').map((line, i) => (
                                <div key={i}>{line}</div>
                              ))}
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="no-messages">
                          <p>Még nincsenek üzenetek ehhez a reklamációhoz.</p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Message Input */}
                  <div className="message-input-container">
                    <div className="message-input-header">
                      <h4>Új üzenet írása</h4>
                      {selectedComplaint.status === 'closed' && (
                        <span className="closed-notice">
                          <FiLock size={16} />
                          Ez a reklamáció le van zárva
                        </span>
                      )}
                    </div>
                    <div className="message-input">
                      <textarea
                        value={messageText}
                        onChange={(e) => setMessageText(e.target.value)}
                        placeholder={selectedComplaint.status === 'closed' ? 'Ez a reklamáció le van zárva, nem lehet több üzenetet küldeni' : 'Írja ide az üzenetet...'}
                        rows={4}
                        disabled={sendingMessage || selectedComplaint.status === 'closed'}
                      />
                      <div className="message-actions">
                        <button
                          onClick={handleSendMessage}
                          disabled={!messageText.trim() || sendingMessage || selectedComplaint.status === 'closed'}
                          className="send-button"
                        >
                          <FiSend />
                          {sendingMessage ? 'Küldés...' : selectedComplaint.status === 'closed' ? 'Reklamáció lezárva' : 'Üzenet elküldése'}
                        </button>
                      </div>
                    </div>
                  </div>
                </>
              ) : (
                <div className="no-selection">
                  <FiMessageCircle size={64} />
                  <h3>Válasszon reklamációt</h3>
                  <p>Kattintson egy reklamációra a bal oldali listában a részletek megtekintéséhez.</p>
                </div>
              )}
            </div>
          </div>
        </div>
  );
};

export default Complaints;